import { UpdateDeviceTelemetryRecord } from '@signageos/actions/dist/Device/Telemetry/deviceTelemetryActions';
import { MonitoringLogData } from '@signageos/common-types/dist/Device/MonitoringLog/MonitoringLogData';
import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import wait from '@signageos/lib/dist/Timer/wait';
import _ from 'lodash';
import { takeEvery } from 'redux-saga/effects';
import { IntervalType, ITelemetryIntervals } from '../../../Device/Configuration/telemetryIntervals';
import { withDependencies } from '../../../DI/dependencyInjection';
import FrontCapability from '../../../NativeDevice/Front/FrontCapability';
import Property from '../../../Property/Property';
import { IPropertyStorage } from '../../../Property/propertyStorage';
import { deliver } from './../../../Socket/socketActionDeliverHelper';
import { DEBOUNCE } from '../../../Intervals/constants';
import IFrontDriver from '../../../NativeDevice/Front/IFrontDriver';
import { UpdateActiveAppletBinary } from '@signageos/actions/dist/Applet/appletActions';
import { registerTelemetry } from '../../../Management/Device/Telemetry/deviceTelemetryHelper';
import Debug from 'debug';

const debug = Debug('@signageos/front-display:Front:Device:Telemetry:deviceTelemetrySagas');

type IGetSettings<T extends DeviceTelemetryType> = () => Promise<MonitoringLogData[T]> | Iterator<unknown>;

export const createFrontDeviceTelemetrySaga = <T extends DeviceTelemetryType>(
	type: T,
	requiredCapabilities: FrontCapability[],
	getSettings: IGetSettings<T>,
) =>
	withDependencies(
		['telemetryIntervals', 'frontDriver', 'propertyStorage'],
		function* ({
			telemetryIntervals,
			frontDriver,
			propertyStorage,
		}: {
			telemetryIntervals: ITelemetryIntervals | null;
			frontDriver: IFrontDriver;
			propertyStorage: IPropertyStorage;
		}) {
			for (const capability of requiredCapabilities) {
				const supported = yield frontDriver.frontSupports(capability);
				if (!supported) {
					return;
				}
			}

			yield takeEvery(Property.TELEMETRY_INVALIDATION, function* () {
				debug(`Received telemetry invalidation for ${type}, forcing telemetry update`);
				yield forceTelemetryUpdate(type, getSettings, propertyStorage);
			});

			const succeedAction = convertTypeToAction(type);
			if (succeedAction) {
				yield takeEvery(succeedAction, function* () {
					debug(`Received ${succeedAction} action, forcing telemetry update for ${type}`);
					yield forceTelemetryUpdate(type, getSettings, propertyStorage);
				});
			}

			const telemetryIntervalType = convertTelemetryTypeToIntervalType(type);
			const periodMs = telemetryIntervals?.[telemetryIntervalType] ?? 0;
			yield registerTelemetry(type, periodMs, function* () {
				try {
					yield* getSettingsAndSendItWhenChanged(type, getSettings, propertyStorage);
				} catch (error) {
					console.error(`reporting device settings ${type} failed`, error);
				}
			});
		},
	);

function* getSettingsAndSendItWhenChanged<T extends DeviceTelemetryType>(
	type: T,
	getSettings: IGetSettings<T>,
	propertyStorage: IPropertyStorage,
) {
	try {
		const property: Property = getTelemetryPropertyName(type, 'LATEST_REPORTED_SETTINGS');
		const cachedDeviceSettings: MonitoringLogData[T] | undefined = yield propertyStorage.getValueOrDefault<
			MonitoringLogData[T] | undefined
		>(property, undefined);
		const currentDeviceSettings: MonitoringLogData[T] = yield getSettings();

		const isDeviceSettingsChanged = !_.isEqual(cachedDeviceSettings, currentDeviceSettings);
		if (isDeviceSettingsChanged) {
			yield deliver<UpdateDeviceTelemetryRecord<T, MonitoringLogData[T]>>({
				type: UpdateDeviceTelemetryRecord,
				name: type,
				data: currentDeviceSettings,
			});

			yield propertyStorage.setValue(property, currentDeviceSettings);
		}
	} catch (error) {
		console.error(`getSettingsAndSendItWhenChanged and sending device telemetry ${type} failed`, error);
	}
}

function* forceTelemetryUpdate<T extends DeviceTelemetryType>(type: T, getSettings: IGetSettings<T>, propertyStorage: IPropertyStorage) {
	const property: Property = getTelemetryPropertyName(type, 'LATEST_REPORTED_SETTINGS');

	// Delay for resolving deviceLog
	yield wait(DEBOUNCE);

	try {
		const currentDeviceSettings: MonitoringLogData[T] = yield getSettings();

		yield deliver<UpdateDeviceTelemetryRecord<T, MonitoringLogData[T]>>({
			type: UpdateDeviceTelemetryRecord,
			name: type,
			data: currentDeviceSettings,
		});

		yield propertyStorage.setValue(property, currentDeviceSettings);
	} catch (error) {
		console.error('forceTelemetryUpdate failed', error);
	}
}

function getTelemetryPropertyName(type: DeviceTelemetryType, prefix: string): Property {
	if (typeof DeviceTelemetryType[type] === 'undefined') {
		throw Error(`Device settings type ${type} does not exist`);
	}
	const propertyKey = `${prefix}_${DeviceTelemetryType[type]}`;
	if (typeof Property[propertyKey as keyof typeof Property] === 'undefined') {
		throw Error(`Property key is not valid ${propertyKey}`);
	}
	return Property[propertyKey as keyof typeof Property];
}

function convertTelemetryTypeToIntervalType<T extends DeviceTelemetryType>(type: T) {
	switch (type) {
		case DeviceTelemetryType.SUPRA:
			return IntervalType.SUPRA;
		default:
			return IntervalType.DEFAULT;
	}
}

function convertTypeToAction<T extends DeviceTelemetryType>(type: T) {
	switch (type) {
		case DeviceTelemetryType.SUPRA:
			return UpdateActiveAppletBinary;
		default:
			return;
	}
}
